import { useState } from "react";
import type { MediaItem } from "../../shared/types.js";

const pick = (m: MediaItem, ...kinds: string[]) =>
  kinds.map((k) => m.renditions.find((r) => r.kind === k)?.url).find(Boolean);

export function MediaTile({
  item,
  onDelete,
  downloadHref,
}: {
  item: MediaItem;
  onDelete?: () => void;
  /** Lien de téléchargement (qualité originale) — affiché seulement côté back-office. */
  downloadHref?: string;
}) {
  const [open, setOpen] = useState(false);
  const thumb = pick(item, "thumb", "poster", "full");
  const mp4 = pick(item, "mp4");
  const full = pick(item, "full", "mp4");
  const pending = item.status === "processing" || item.status === "uploading";
  const canDownload = !!downloadHref && item.status !== "uploading";

  return (
    <>
      <figure className="group relative overflow-hidden rounded-xl border border-stone-200 bg-stone-100">
        {pending ? (
          <div className="flex aspect-square items-center justify-center text-xs text-stone-400">
            traitement…
          </div>
        ) : item.status === "failed" ? (
          <div className="flex aspect-square items-center justify-center text-xs text-rose-400">
            échec
          </div>
        ) : (
          <button onClick={() => setOpen(true)} className="block w-full">
            <img
              src={thumb}
              alt={item.note ?? item.pseudo}
              className="aspect-square w-full object-cover"
            />
            {item.kind === "video" && (
              <span className="absolute inset-0 flex items-center justify-center text-3xl text-white/90 drop-shadow">
                ▶
              </span>
            )}
          </button>
        )}
        <figcaption className="bg-white p-2 text-xs">
          <span className="font-medium text-stone-800" title={item.email ?? undefined}>
            {item.pseudo}
          </span>
          {item.email && <span className="block truncate text-[10px] text-stone-400">{item.email}</span>}
          {item.note && <span className="block text-stone-500">{item.note}</span>}
        </figcaption>
        {canDownload && (
          <a
            href={downloadHref}
            className="absolute left-1 top-1 rounded-full bg-black/60 px-2 text-xs text-white opacity-0 transition group-hover:opacity-100"
            aria-label="Télécharger"
            title="Télécharger l'original"
          >
            ⬇
          </a>
        )}
        {onDelete && (
          <button
            onClick={onDelete}
            className="absolute right-1 top-1 rounded-full bg-black/60 px-2 text-xs text-white opacity-0 transition group-hover:opacity-100"
            aria-label="Supprimer"
          >
            ✕
          </button>
        )}
      </figure>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4"
          onClick={() => setOpen(false)}
        >
          {item.kind === "video" && mp4 ? (
            <video src={mp4} controls autoPlay playsInline className="max-h-full max-w-full rounded" />
          ) : (
            <img src={full} alt="" className="max-h-full max-w-full rounded" />
          )}
          {canDownload && (
            <a
              href={downloadHref}
              onClick={(e) => e.stopPropagation()}
              className="absolute bottom-6 right-6 rounded-full bg-white px-4 py-2 text-sm font-medium text-stone-800 shadow-lg"
            >
              ⬇ Télécharger
            </a>
          )}
        </div>
      )}
    </>
  );
}
